package com.appsyde.shared.presentation.screen.screen

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.appsyde.shared.modules.account.domain.model.Me
import com.appsyde.shared.modules.account.domain.model.MeAccount
import com.appsyde.shared.modules.account.domain.model.MeUser
import com.appsyde.shared.presentation.style.style.AppStylesAccount
import com.appsyde.shared.presentation.style.style.AppStylesGlobal
import com.appsyde.shared.presentation.style.style.AppStylesHeader
import com.appsyde.shared.presentation.style.style.AppStylesLayout
import com.appsyde.shared.presentation.style.style.AppStylesWizard
import com.appsyde.shared.presentation.screen.screen.inbox.InboxPermissionsRequiredView
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.StyleSheet
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.border
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.flexShrink
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.fontWeight
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.overflowY
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.dom.*

/**
 * Page démo pour comparer les écrans (onboarding, permissions, invitation, succès, etc.).
 * Usage local uniquement : flèches pour passer d'un écran à l'autre, aucun appel API.
 * Accès : /demo
 */
private const val DEMO_SCREEN_COUNT = 7

@Composable
fun ScreensDemoPage() {
    Style(DemoStyles)
    var currentIndex by remember { mutableStateOf(0) }
    val index = currentIndex.coerceIn(0, DEMO_SCREEN_COUNT - 1)
    val labels = listOf(
        "Permissions Gmail",
        "Onboarding – Invite équipe",
        "Onboarding – Succès",
        "Aucun workspace",
        "Acceptation invitation (page réelle)",
        "Acceptation invitation – Rejoindre l'équipe",
        "Billing (abonnement requis)"
    )

    Div(attrs = { classes(DemoStyles.demoRoot) }) {
        Div(attrs = { classes(DemoStyles.demoNav) }) {
            Button(attrs = {
                classes(DemoStyles.demoNavBtn)
                attr("type", "button")
                if (index == 0) attr("disabled", "true")
                onClick { currentIndex = (index - 1).coerceAtLeast(0) }
            }) { Text("← Précédent") }
            Span(attrs = { classes(DemoStyles.demoNavTitle) }) {
                Text("${index + 1} / $DEMO_SCREEN_COUNT — ${labels[index]}")
            }
            Button(attrs = {
                classes(DemoStyles.demoNavBtn)
                attr("type", "button")
                if (index == DEMO_SCREEN_COUNT - 1) attr("disabled", "true")
                onClick { currentIndex = (index + 1).coerceAtMost(DEMO_SCREEN_COUNT - 1) }
            }) { Text("Suivant →") }
        }
        Div(attrs = { classes(DemoStyles.demoContent) }) {
            key(index) {
                when (index) {
                    0 -> InboxPermissionsRequiredView(
                        onGrantPermissions = {},
                        onLogout = {},
                        title = "Autorisez l'accès à Gmail",
                        showPrimaryButton = true
                    )
                    1 -> OnboardingWizardScreen(onComplete = {})
                    2 -> DemoOnboardingSuccessScreen()
                    3 -> NoWorkspaceScreen()
                    4 -> InviteAcceptPage()
                    5 -> DemoInviteAcceptRejoinScreen()
                    6 -> {
                        val fakeMe = Me(
                            user = MeUser("demo", "demo@example.com", "Demo User"),
                            account = MeAccount("acc-demo", "demo@example.com"),
                            role = "OWNER",
                            onboardingStatus = "READY",
                            mustSubscribe = true,
                            gmailConnectionStatus = "OK",
                            isOwner = true,
                            workspaceDisplayName = "Workspace démo",
                            membersCount = 1
                        )
                        BillingGateScreen(meInfo = fakeMe, onLogout = {})
                    }
                    else -> {}
                }
            }
        }
    }
}

/** Réplique visuelle de l’écran « Votre boîte est prête » sans logique (démo uniquement). */
@Composable
private fun DemoOnboardingSuccessScreen() {
    Style(AppStylesGlobal)
    Style(AppStylesHeader)
    Style(AppStylesLayout)
    Style(AppStylesWizard)

    Div(attrs = { classes(AppStylesGlobal.appRoot) }) {
        Div(attrs = { classes(AppStylesHeader.header) }) {
            Div(attrs = { classes(AppStylesHeader.wizardHeaderInner) }) {
                Div(attrs = { classes(AppStylesHeader.brandRow) }) {
                    Img("/appsydeIcon.png", attrs = {
                        classes(AppStylesHeader.brandIcon)
                        attr("alt", "AppSyde")
                    })
                    Div(attrs = { classes(AppStylesHeader.brandCol) }) {
                        Div(attrs = { classes(AppStylesHeader.brand) }) { Text("AppSyde") }
                        Span(attrs = { classes(AppStylesHeader.brandSubtitle) }) { Text("Configuration") }
                    }
                }
                Span(attrs = { classes(AppStylesWizard.wizardCaption) }) { Text("Setup 2/2") }
                Span() {}
            }
        }
        Div(attrs = {
            classes(
                AppStylesGlobal.global,
                AppStylesLayout.globalContentWrap,
                AppStylesLayout.wizardPageWrap
            )
        }) {
            Div(attrs = { classes(AppStylesLayout.wizardContentWrapCentered) }) {
                Div(attrs = { classes(AppStylesLayout.wizardCardWrap) }) {
                    Div(attrs = { classes(AppStylesWizard.wizardCard) }) {
                        Div(attrs = { classes(AppStylesWizard.wizardCardInner) }) {
                            Div(attrs = { classes(AppStylesWizard.wizardCardHeader) }) {
                                Div(attrs = { classes(AppStylesWizard.wizardStepperWrap) }) {
                                    Div(attrs = { classes(AppStylesWizard.wizardStepper) }) {
                                        Div(attrs = {
                                            classes(
                                                AppStylesWizard.wizardStepperDot,
                                                AppStylesWizard.wizardStepperDotActive
                                            )
                                        }) {}
                                        Span(attrs = { classes(AppStylesWizard.wizardStepperLabel) }) {
                                            Text("Configuration")
                                        }
                                        Div(attrs = { classes(AppStylesWizard.wizardStepperLine) }) {
                                            Div(attrs = {
                                                classes(
                                                    AppStylesWizard.wizardStepperLineProgress
                                                )
                                                attr("style", "width: 100%")
                                            }) {}
                                        }
                                        Div(attrs = {
                                            classes(
                                                AppStylesWizard.wizardStepperDot,
                                                AppStylesWizard.wizardStepperDotActive
                                            )
                                        }) {}
                                        Span(attrs = { classes(AppStylesWizard.wizardStepperLabel) }) {
                                            Text("Terminé")
                                        }
                                    }
                                }
                            }
                            Div(attrs = {
                                classes(
                                    AppStylesWizard.wizardCardBody,
                                    AppStylesWizard.wizardCardBodyCentered,
                                    AppStylesWizard.wizardReadyBody
                                )
                            }) {
                                Div(attrs = { classes(AppStylesWizard.wizardReadyBlock) }) {
                                    Div(attrs = { classes(AppStylesWizard.wizardReadyIconWrap) }) {
                                        Span(attrs = { classes(AppStylesWizard.wizardReadyIcon) }) {
                                            Text("✔")
                                        }
                                    }
                                    Div(attrs = {
                                        classes(
                                            AppStylesWizard.wizardReadyTitle,
                                            AppStylesWizard.wizardReadyTitleSpacing
                                        )
                                    }) {
                                        Text("Votre boîte est prête")
                                    }
                                    Div(attrs = {
                                        classes(
                                            AppStylesWizard.wizardReadySub,
                                            AppStylesWizard.wizardReadySubWide
                                        )
                                    }) {
                                        Text("Les emails Gmail sont maintenant connectés et vous pouvez commencer à répondre.")
                                    }
                                    Div(attrs = {
                                        classes(
                                            AppStylesWizard.wizardReadySub,
                                            AppStylesWizard.wizardReadySubWide
                                        )
                                    }) {
                                        Text("AppSyde affiche vos tickets et prépare les réponses.")
                                    }
                                    Div(attrs = { classes(AppStylesWizard.wizardReadyChecklist) }) {
                                        Div(attrs = { classes(AppStylesWizard.wizardReadyCheckItem) }) {
                                            Span(attrs = { classes(AppStylesWizard.wizardReadyCheckMark) }) {
                                                Text("✓")
                                            }
                                            Text("Gmail connecté")
                                        }
                                        Div(attrs = { classes(AppStylesWizard.wizardReadyCheckItem) }) {
                                            Span(attrs = { classes(AppStylesWizard.wizardReadyCheckMark) }) {
                                                Text("✓")
                                            }
                                            Text("Équipe configurée")
                                        }
                                        Div(attrs = { classes(AppStylesWizard.wizardReadyCheckItem) }) {
                                            Span(attrs = { classes(AppStylesWizard.wizardReadyCheckMark) }) {
                                                Text("✓")
                                            }
                                            Text("AppSyde prêt")
                                        }
                                    }
                                }
                            }
                            Div(attrs = {
                                classes(
                                    AppStylesWizard.wizardCardFooterCenter,
                                    AppStylesWizard.wizardReadyFooter
                                )
                            }) {
                                Button(attrs = {
                                    classes(AppStylesWizard.wizardBtnPrimaryWide)
                                    attr("type", "button")
                                }) { Text("Aller à la boîte") }
                            }
                        }
                    }
                }
            }
        }
    }
}

/** Réplique visuelle de la page « Rejoindre l'équipe » (acceptation d'invitation) pour la démo. */
@Composable
private fun DemoInviteAcceptRejoinScreen() {
    Style(AppStylesGlobal)
    Style(AppStylesHeader)
    Style(AppStylesLayout)
    Style(AppStylesWizard)
    Style(AppStylesAccount)

    Div(attrs = { classes(AppStylesGlobal.appRoot) }) {
        Div(attrs = { classes(AppStylesHeader.header) }) {
            Div(attrs = { classes(AppStylesAccount.invitePageHeaderInner) }) {
                Div(attrs = { classes(AppStylesHeader.brandRow) }) {
                    Img("/appsydeIcon.png", attrs = {
                        classes(AppStylesHeader.brandIcon)
                        attr("alt", "AppSyde")
                    })
                    Div(attrs = { classes(AppStylesHeader.brandCol) }) {
                        Div(attrs = { classes(AppStylesHeader.brand) }) { Text("AppSyde") }
                        Span(attrs = { classes(AppStylesHeader.brandSubtitle) }) { Text("Invitation") }
                    }
                }
                Span(attrs = { classes(AppStylesAccount.invitePageHeaderRight) }) { Text("Rejoindre une équipe") }
            }
        }
        Div(attrs = { classes(AppStylesGlobal.global, AppStylesLayout.globalContentWrap, AppStylesLayout.wizardPageWrap) }) {
            Div(attrs = { classes(AppStylesLayout.wizardContentWrapCentered) }) {
                Div(attrs = { classes(AppStylesAccount.inviteCardWrap) }) {
                    Div(attrs = { classes(AppStylesAccount.inviteCard) }) {
                        Div(attrs = { classes(AppStylesAccount.inviteCardInner) }) {
                            Div(attrs = { classes(AppStylesAccount.inviteTitleRow) }) {
                                Div(attrs = { classes(AppStylesAccount.inviteHeroBadge) }) {
                                    Span(attrs = { classes(AppStylesAccount.inviteHeroBadgeIcon) }) { Text("👥") }
                                }
                                Div(attrs = { classes(AppStylesAccount.inviteTitle) }) {
                                    Text("Rejoindre l'équipe")
                                }
                            }
                            Div(attrs = { classes(AppStylesAccount.inviteDescription) }) {
                                Span(attrs = { classes(AppStylesAccount.inviteInviterName) }) { Text("Marie Dupont") }
                                Text(" vous a invité à accéder à cette boîte support dans AppSyde.")
                            }
                            Div(attrs = { classes(AppStylesAccount.inviteDescription) }) {
                                Text("Connectez-vous avec le compte Google invité pour continuer.")
                            }
                            Div(attrs = { classes(AppStylesAccount.inviteSupportBlock) }) {
                                Div(attrs = { classes(AppStylesAccount.inviteSupportLabel) }) { Text("BOÎTE SUPPORT") }
                                Div(attrs = { classes(AppStylesAccount.inviteSupportValue) }) { Text("support@marque.com") }
                            }
                            Div(attrs = { classes(AppStylesAccount.inviteActionWrap) }) {
                                Button(attrs = {
                                    classes(AppStylesAccount.inviteBtnPrimary)
                                    attr("type", "button")
                                }) { Text("Se connecter avec Google") }
                                Span(attrs = { classes(AppStylesAccount.inviteActionSpacer) }) {}
                                Button(attrs = {
                                    classes(AppStylesAccount.inviteLinkSecondary)
                                    attr("type", "button")
                                }) { Text("Déjà connecté ? Accepter l'invitation") }
                            }
                            Div(attrs = { classes(AppStylesAccount.inviteTrustRow) }) {
                                Span(attrs = { classes(AppStylesAccount.inviteTrustCheck) }) { Text("✓") }
                                Text("Session sécurisée. L'accès est accordé uniquement après validation.")
                            }
                        }
                    }
                }
            }
        }
    }
}

private object DemoStyles : StyleSheet() {
    val demoRoot by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        property("min-height", "100vh")
    }
    val demoNav by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(16.px)
        padding(12.px, 16.px)
        backgroundColor(org.jetbrains.compose.web.css.Color("#f1f5f9"))
        property("border-bottom", "1px solid rgba(15,23,42,0.08)")
        flexShrink(0)
    }
    val demoNavBtn by style {
        padding(8.px, 16.px)
        borderRadius(8.px)
        border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, org.jetbrains.compose.web.css.Color("rgba(15,23,42,0.2)"))
        backgroundColor(org.jetbrains.compose.web.css.Color.white)
        fontSize(14.px)
        fontWeight(600)
        property("cursor", "pointer")
    }
    val demoNavTitle by style {
        fontSize(14.px)
        fontWeight(600)
        color(org.jetbrains.compose.web.css.Color("rgba(15,23,42,0.8)"))
        property("min-width", "280px")
        property("text-align", "center")
    }
    val demoContent by style {
        property("flex", "1")
        minHeight(0.px)
        overflowY("auto")
    }
}
